import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertCircle } from "lucide-react";

interface ReprovacaoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (data: {
    tipo_reprovacao: string;
    reprovado_por: 'corisco' | 'cliente';
    motivo_reprovacao: string;
  }) => void;
  isLoading?: boolean;
}

const tiposReprovacao = [
  {
    value: 'corisco_falta_perfis_adequados',
    label: '[CORISCO] Vaga declinada por falta de perfis adequados no banco de talentos',
    reprovado_por: 'corisco' as const,
  },
  {
    value: 'corisco_falta_disponibilidade_perfis',
    label: '[CORISCO] Vaga declinada por falta de disponibilidade dos perfis selecionados',
    reprovado_por: 'corisco' as const,
  },
  {
    value: 'corisco_indicacoes_fora_prazo',
    label: '[CORISCO] Vaga caiu, pois as indicações não foram feitas em tempo hábil',
    reprovado_por: 'corisco' as const,
  },
  {
    value: 'cliente_perfis_nao_aprovados',
    label: '[CLIENTE] Vaga caiu, pois os perfis indicados não foram aprovados pelo cliente',
    reprovado_por: 'cliente' as const,
  },
  {
    value: 'cliente_sem_resposta_perfis',
    label: '[CLIENTE] Vaga caiu, pois não houve resposta por parte do cliente sobre os perfis indicados',
    reprovado_por: 'cliente' as const,
  },
  {
    value: 'cliente_motivos_alheios_indicacoes',
    label: '[CLIENTE] Vaga caiu, por motivos alheios às indicações',
    reprovado_por: 'cliente' as const,
  },
];

export function ReprovacaoModal({
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
}: ReprovacaoModalProps) {
  const [tipoReprovacao, setTipoReprovacao] = useState<string>("");
  const [motivoReprovacao, setMotivoReprovacao] = useState<string>("");

  const handleConfirm = () => {
    if (!tipoReprovacao || !motivoReprovacao.trim()) {
      return;
    }

    const tipoSelecionado = tiposReprovacao.find(t => t.value === tipoReprovacao);
    if (!tipoSelecionado) return;

    onConfirm({
      tipo_reprovacao: tipoReprovacao,
      reprovado_por: tipoSelecionado.reprovado_por,
      motivo_reprovacao: motivoReprovacao.trim(),
    });

    // Reset form
    setTipoReprovacao("");
    setMotivoReprovacao("");
  };

  const handleCancel = () => {
    setTipoReprovacao("");
    setMotivoReprovacao("");
    onOpenChange(false);
  };

  const isFormValid = tipoReprovacao && motivoReprovacao.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-5 w-5" />
            Reprovar Vaga
          </DialogTitle>
          <DialogDescription>
            Informe o motivo da reprovação desta vaga. Estas informações serão registradas no histórico.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="tipo_reprovacao">
              Tipo de Reprovação <span className="text-red-500">*</span>
            </Label>
            <Select
              value={tipoReprovacao}
              onValueChange={setTipoReprovacao}
              disabled={isLoading}
            >
              <SelectTrigger id="tipo_reprovacao">
                <SelectValue placeholder="Selecione o tipo de reprovação" />
              </SelectTrigger>
              <SelectContent>
                {tiposReprovacao.map((tipo) => (
                  <SelectItem key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="motivo_reprovacao">
              Detalhes do Motivo <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="motivo_reprovacao"
              placeholder="Descreva os detalhes do motivo da reprovação..."
              value={motivoReprovacao}
              onChange={(e) => setMotivoReprovacao(e.target.value)}
              disabled={isLoading}
              rows={5}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Forneça informações detalhadas sobre o motivo da reprovação
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={!isFormValid || isLoading}
          >
            {isLoading ? "Reprovando..." : "Confirmar Reprovação"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

